import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, map, Observable, OperatorFunction } from 'rxjs';
import { BannerAppComponent } from '../../../components/banner-app/banner-app.component';
import { FooterAppComponent } from '../../../components/footer-app/footer-app.component';
import { getVehicleData } from '../../../helpers/dataLayerHelpers';
import {
  getFromStorage,
  getSessionId,
  saveToStorage
} from '../../../helpers/storageUtils';
import { DatosGenerales } from '../../../Models/datosGenerales';
import { ONEROSO, YATEMENOSDATOS } from '../../../routes.constans';
import { DataLayerService } from '../../../services/DataLayer/data-layer.service';
import { ExceptionService } from '../../../services/excepciones/exception.service';
import { InactivityService } from '../../../services/inactivity/inactivity.service';
import { selectData } from '../../../storage/Lead/data.selectors';
import { StepperComponent } from '../stepper/stepper.component';

export interface Bancos {
  Id: string;
  Name: string;
}

@Component({
  selector: 'app-consulta-runt',
  standalone: true,
  imports: [
    BannerAppComponent,
    FooterAppComponent,
    ReactiveFormsModule,
    CommonModule,
    StepperComponent,
    NgbTypeaheadModule,
  ],
  templateUrl: './consulta-runt.component.html',
  styleUrl: './consulta-runt.component.scss',
})
export class ConsultaRuntComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly inactivityService = inject(InactivityService);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly exceptionService = inject(ExceptionService);

  @ViewChild('bancoPrenda') bancoPrenda!: ElementRef;

  sessionId = getSessionId().toString();
  leadId = '';
  codAsesor = '';
  
  // Observable del store
  datosGenerales$: Observable<DatosGenerales | null> = this.store.select(selectData);
  datosGenerales: DatosGenerales | null = null;

  entidadBancaria = '';
  swVisibleDatosBancarios = false;

  states: string[] = [];
  bancos: Bancos[] = [];

  formulario = new FormGroup({
    bancoPrenda: new FormControl(''),
  });

  motoData: Record<string, string | number> = {};
  orderedMotoDataKeys: string[] = [];

  ownerData: Record<string, string | number> = {};
  orderedOwnerDataKeys: string[] = [];

  valBanco = false;
  valBanco2 = true;

  constructor() {
    this.datosGenerales$.pipe(takeUntilDestroyed()).subscribe((data) => {
      this.handleLeadDataUpdate(data);
    });

    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.codAsesor = params['codigoAsesor'] || '';
      console.log('Código Asesor en Loader:', this.codAsesor);
    });
  }

  ngOnInit(): void {
    this.setupInactivity();
  }

  private handleLeadDataUpdate(data: DatosGenerales | null): void {
    this.datosGenerales = data;
    if (!data?.LeadResponse?.data?.vehicle) return;

    const vehicle = data.LeadResponse.data.vehicle;
    const owner = vehicle.owner;

    this.motoData = {
      Placa: vehicle.plate || '',
      Modelo: vehicle.model || '',
      Marca: vehicle.brand || '',
      Cilindraje: vehicle.cylinder || '',
    };
    this.orderedMotoDataKeys = ['Placa', 'Modelo', 'Marca', 'Cilindraje'];

    this.ownerData = {
      Nombre: owner?.firstnames || '',
      Apellido: owner?.lastnames || '',
      Documento: `${owner?.document?.type || ''} ${owner?.document?.number || ''}`,
    };
    this.orderedOwnerDataKeys = ['Nombre', 'Apellido', 'Documento'];

    this.pushDataLayer('Pageview');

    // Lógica Prenda
    this.handlePrendaLogic(data);

    // Guardar Session ID si existe
    if (this.datosGenerales?.LeadResponse.data.Id) {
      saveToStorage('LeadId', this.datosGenerales?.LeadResponse.data.Id, 'session');
    }
  }

  private handlePrendaLogic(data: DatosGenerales): void {
    if (data.LeadResponse?.data?.vehicle?.Prenda === 'SI') {
      saveToStorage('Prenda', data.LeadResponse.data.vehicle.Prenda, 'session');
      this.swVisibleDatosBancarios = true;

      const prendario = data.LeadResponse.additionalInfo?.prendario;
      this.entidadBancaria = typeof prendario === 'string' ? prendario : '';

      if (prendario && typeof prendario !== 'string') {
        try {
          this.bancos = JSON.parse(JSON.stringify(prendario));
          // FIX: Populate states for typeahead/validation
          if (this.bancos.length > 0) {
            this.states = this.bancos.map(b => b.Name);
          }
        } catch (error) {
          console.error('Error procesando bancos:', error);
        }
      }
    }
    saveToStorage('motosData', this.motoData, 'session');
  }

  private setupInactivity(): void {
    this.inactivityService.startDefaultTimerForScreen(
      'Datos-RUNT',
      'Abandono Datos RUNT',
      '30'
    );
    
    if (this.datosGenerales) {
      this.inactivityService.setLeadData(this.datosGenerales);
    }
  }

  search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map((term) =>
        term.length < 2
          ? []
          : this.states
              .filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
              .slice(0, 10),
      ),
    );

  validateBankSelection(): void {
    const bancoValue = this.formulario.get('bancoPrenda')?.value;
    console.log('Valor del banco seleccionado:', bancoValue);

    if (!bancoValue) {
      this.valBanco = true;
      this.valBanco2 = false;
      this.entidadBancaria = '';
    } else if (!this.states.includes(bancoValue)) {
      this.valBanco = true;
      this.valBanco2 = false;
      this.entidadBancaria = '';
    } else {
      const bancoSeleccionado = this.bancos.find((banco) => banco.Name === bancoValue);
      if (bancoSeleccionado) {
        this.entidadBancaria = bancoSeleccionado.Name;
        this.valBanco = false;
        this.valBanco2 = true;
        console.log('Entidad bancaria actualizada:', this.entidadBancaria);
      }
    }
  }

  get isBankInvalid(): boolean {
    const control = this.formulario.get('bancoPrenda');
    return !!(control?.value === '' && control?.touched);
  }

  /**
   * Rescate de Lead: El usuario indica que los datos no coinciden.
   */
  async onDataMismatch() {
    this.pushDataLayer('Button', 'No coinciden');
    console.log('🚨 Usuario indicó que los datos RUNT no coinciden - Iniciando rescate de lead');

    this.inactivityService.stopTimer();

    const sessionId = getSessionId().toString();
    const leadId = this.datosGenerales?.LeadResponse?.data?.Id || '';

    this.exceptionService.sendGeneralException(
      sessionId,
      leadId,
      {
        required_status: 'Datos RUNT no coinciden',
        percentege_lead: 35,
        estadoRetoma: 'Inhabilitado',
        estadoRescate: 'Habilitado',
        mobile_phone: this.datosGenerales?.Lead?.mobile_phone || '',
        product: 'Motos Digital',
        name: this.datosGenerales?.Lead?.name || '',
        plate: this.datosGenerales?.Lead?.plate || '',
        pantalla: 'Datos-RUNT',
      }
    ).subscribe({
      next: (response) => console.log('✅ Excepción de rescate enviada:', response),
      error: (error) => console.error('❌ Error al enviar excepción:', error),
    });

    this.router.navigate([YATEMENOSDATOS]);
  }

  onDataConfirm() {
    this.pushDataLayer('Button', 'Si, continuar');

    if (this.datosGenerales?.LeadResponse?.additionalInfo?.isPrenda === 'SI') {
      saveToStorage('Prenda', true, 'session');
    }
    
    const codigoAsesor = getFromStorage('codigoAsesor');
    if (codigoAsesor) {
      this.router.navigate([ONEROSO, { codigoAsesor }]);
    } else {
      this.router.navigate([ONEROSO]);
    }
  }

  get isButtonDisabled(): boolean {
    if (this.datosGenerales?.LeadResponse?.data?.vehicle?.Prenda !== 'SI') {
      return false;
    }

    const prendario = this.datosGenerales?.LeadResponse?.additionalInfo?.prendario;
    const bancoSeleccionado = this.formulario.get('bancoPrenda')?.value;

    if (!Array.isArray(prendario) && prendario) {
      return false;
    }

    if (Array.isArray(prendario)) {
      return !bancoSeleccionado || !this.states.includes(bancoSeleccionado);
    }

    return true;
  }

  private pushDataLayer(ecom_item: 'Pageview' | 'Button' | undefined, ecom_subitem = ''): void {
    this.dataLayerService.pushDataLayer({
      event: 'moto_ecommerce_step_2_datos_runt',
      ecom_tipo: 'Moto',
      ecom_item,
      ecom_subitem,
      ecom_pantalla: 'Datos RUNT',
      user_id: this.sessionId,
      ...getVehicleData(this.datosGenerales),
    });
  }
}
